import { Star } from "lucide-react";
import { motion } from "motion/react";
import { Card } from "@speakai/ui/card";
import { useTranslation } from "react-i18next";
import { TESTIMONIALS } from "@/features/pricing/pricing-data";

/**
 * Shared social-proof sections, single source of truth for the pricing page and
 * the public /claim-offer flow. Copy/figures live here so the two surfaces never
 * drift. Figures are the same ones shown on the pricing page.
 */

export function TrustBar() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4 border-y border-border py-3 text-xs text-muted-foreground md:gap-8">
      <div className="flex items-center gap-1.5">
        <div className="flex gap-0.5" role="img" aria-label="5 out of 5 stars">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className="h-3.5 w-3.5 fill-warning text-warning"
              aria-hidden="true"
            />
          ))}
        </div>
        <span>
          <strong className="font-bold text-foreground">4.9/5</strong> on G2
        </span>
      </div>
      <div>
        <strong className="font-bold text-foreground">250,000+</strong> users
      </div>
      <div>
        <strong className="font-bold text-foreground">3,000+</strong> customers
      </div>
      <div>
        <strong className="font-bold text-foreground">100+</strong> languages
      </div>
    </div>
  );
}

/**
 * @param animate - When true (pricing page), cards fade in on scroll via
 *   `whileInView`. The short /claim-offer page can render with the cards already
 *   in view before the observer fires, leaving them stuck at opacity 0, so it
 *   passes `animate={false}` to render them statically visible.
 */
export function TestimonialsSection({ animate = true }: { animate?: boolean }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-6 text-center">
      <h2 className="text-2xl font-bold">{t("PRICING.WHAT_CUSTOMERS_SAY")}</h2>
      <div className="mx-auto grid max-w-4xl grid-cols-1 gap-5 md:grid-cols-3">
        {TESTIMONIALS.map((testimonial, i) => {
          const card = (
            <Card className="flex h-full flex-col gap-3 border border-border bg-card p-5 text-left">
              <div className="flex gap-0.5" role="img" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star
                    key={j}
                    className="h-3.5 w-3.5 fill-warning text-warning"
                    aria-hidden="true"
                  />
                ))}
              </div>
              <p className="text-sm italic leading-relaxed text-muted-foreground">
                &ldquo;{testimonial.quote}&rdquo;
              </p>
              <div className="mt-auto">
                <p className="text-sm font-semibold">{testimonial.name}</p>
                <p className="text-xs text-muted-foreground">{testimonial.role}</p>
              </div>
            </Card>
          );

          if (!animate) {
            return (
              <div key={testimonial.name} className="h-full">
                {card}
              </div>
            );
          }

          return (
            <motion.div
              key={testimonial.name}
              className="h-full"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
            >
              {card}
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
